"use client";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Invoice, Customer, Vehicle } from "@/types";

interface InvoiceDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice | null;
  customers: Customer[];
  vehicles: Vehicle[];
  getStatusBadge: (status: string) => JSX.Element;
  onDownload: (invoice: Invoice) => void;
}

export function InvoiceDetailDialog({
  open,
  onOpenChange,
  invoice,
  customers,
  vehicles,
  getStatusBadge,
  onDownload,
}: InvoiceDetailDialogProps) {
  if (!invoice) return null;

  const customer = customers.find((c) => c.id === invoice.customerId);
  const vehicle = vehicles.find((v) => v.id === invoice.vehicleId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Invoice Details</DialogTitle>
          <DialogDescription>{invoice.invoiceNumber}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex justify-between items-start border-b pb-4">
            <div>
              <h3 className="text-lg font-bold">WeClean Car Wash</h3>
              <p className="text-sm text-muted-foreground">We come to you</p>
              <p className="text-sm text-muted-foreground">+974 7090 8373</p>
              <p className="text-sm text-muted-foreground">
                www.weclean.com.qa
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Invoice Date</p>
              <p className="font-medium">{formatDate(invoice.date)}</p>
              <p className="text-sm text-muted-foreground mt-2">Invoice #</p>
              <p className="font-medium">{invoice.invoiceNumber}</p>
            </div>
          </div>

          {/* Bill To */}
          <div>
            <Label className="text-muted-foreground">Bill To:</Label>
            <div className="mt-2">
              <p className="font-medium">{customer?.name || "N/A"}</p>
              <p className="text-sm text-muted-foreground">
                {customer?.phone || ""}
              </p>
              <p className="text-sm text-muted-foreground">
                {customer?.email || ""}
              </p>
            </div>
          </div>

          {/* Vehicle Info */}
          <div>
            <Label className="text-muted-foreground">Vehicle:</Label>
            <p className="mt-1">
              {vehicle
                ? `${vehicle.make} ${vehicle.model} - ${vehicle.plateNumber}`
                : "N/A"}
            </p>
          </div>

          {/* Items Table */}
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.items.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-center">{item.quantity}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.unitPrice)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(item.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="border-t pt-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>{formatCurrency(invoice.subtotal)}</span>
                </div>
                {invoice.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount:</span>
                    <span>-{formatCurrency(invoice.discount)}</span>
                  </div>
                )}
                {invoice.tax > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax:</span>
                    <span>{formatCurrency(invoice.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{formatCurrency(invoice.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="flex justify-between items-center border-t pt-4">
            <div>
              <Label className="text-muted-foreground">Payment Method:</Label>
              <p className="mt-1 capitalize">
                {invoice.paymentMethod || "-"}
              </p>
            </div>
            <div className="text-right">
              <Label className="text-muted-foreground">Payment Status:</Label>
              <div className="mt-1">{getStatusBadge(invoice.paymentStatus)}</div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="border-t pt-4">
              <Label className="text-muted-foreground">Notes:</Label>
              <p className="text-sm mt-1">{invoice.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onDownload(invoice)}>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

